/* Devices. The core loop — the screen that has to be good enough to use daily,
   because if the register is not maintained none of the rest matters (§17).

   Also the QR label target: /devices/:tag opens straight into the drawer, which
   is what makes scanning a label on the workshop floor worth doing (§12). */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDepotScope, scoped } from '../../hooks/useDepotScope';
import { useDepots } from '../../hooks/useDepots';
import { daysUntil, expiryTone, fmtDate, yearsOld, daysSince } from '../../lib/format';
import type { IDevice, Tone } from '../../models';
import { useOpenRecord } from '../../hooks/useOpenRecord';
import { qk } from '../../query/keys';
import { BulkBar, useBulk } from './BulkBar';
import { useServices } from '../../services/context';
import {
  Badge,
  Button,
  Controls,
  DataTable,
  EmptyState,
  Panel,
  SearchBox,
  Segmented,
  Select,
  TableSkeleton,
  type Column,
  LoadError,
} from '../ui';
import { DeviceDetail } from './DeviceDetail';
import { EditDrawer } from './EditDrawer';
import { Explain } from './Explain';
import { hasLeftIntune, hasLeftNinja, renamedTo } from '../../lib/registers';
import { ENDPOINT_TYPES, groupOf } from '../../lib/deviceGroups';
import { DEVICE_SEEN, DEVICE_STATUS, deviceFields, valuesSeen } from '../../lib/registerFields';
import s from './screens.module.scss';
import { PRODUCT } from '../../lib/product';

/** Anything that has not checked in for this long wants chasing. */
const STALE_DAYS = 30;

/** Drawer glyph per device type — the prototype's `.dr-av` carries an icon for
    kit and initials for people. */
const DEVICE_ICON: Partial<Record<IDevice['type'], string>> = {
  Laptop: 'ti-device-laptop',
  Desktop: 'ti-device-desktop',
  Mobile: 'ti-device-mobile',
  Tablet: 'ti-device-tablet',
  Server: 'ti-server',
  Firewall: 'ti-shield-lock',
  Switch: 'ti-network',
  'Access point': 'ti-wifi',
  Printer: 'ti-printer',
  Storage: 'ti-database',
  UPS: 'ti-battery-charging',
};

function complianceTone(value: string): Tone {
  if (value === 'Compliant') return 'ok';
  if (value === 'Non-compliant') return 'bad';
  if (value === 'N/A') return 'muted';
  return 'warn';
}

export function DevicesScreen() {
  const svc = useServices();
  const { scope } = useDepotScope();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { tag } = useParams();

  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const [lens, setLens] = useState('all');
  const [selected, setSelected] = useState<IDevice | null>(null);
  /* The detail drawer stays as it is — it carries attachments and the assigned
     holder, which a generic form has no place for. Add and edit open the shared
     drawer alongside it. */
  const [editing, setEditing] = useState<IDevice | null>(null);
  const [adding, setAdding] = useState(false);

  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });
  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });
  /* For the subnet a machine answers on. Cheap — the networks record is three
     rows and is pinned for offline anyway. */


  /* Deep link from a QR label. Resolves the tag once the list is in, so it works
     from the IndexedDB cache with no network — which is the whole point of
     scanning a label in a yard. */
  useEffect(() => {
    if (!tag || !devices.data) return;
    const match = devices.data.find((d) => d.tag.toLowerCase() === tag.toLowerCase());
    if (match) {
      setSelected(match);
    }
  }, [tag, devices.data]);

  const personName = useMemo(() => {
    const map = new Map((people.data ?? []).map((p) => [p.id, p.name]));
    return (id: string | null) => (id ? (map.get(id) ?? '—') : '—');
  }, [people.data]);

  const types = useMemo(() => [...new Set((devices.data ?? []).map((d) => d.type))].sort(), [devices.data]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return scoped(devices.data ?? [], scope).filter((d) => {
      /* Endpoints only. A server used to be here and on Infrastructure, and a
         printer on all three — three lists showing overlapping subsets of one
         register, which is worse than one list because "where do I look" stops
         having an answer. */
      if (groupOf(d.type) !== 'endpoint') return false;
      if (type !== 'all' && d.type !== type) return false;
      /* The three questions actually asked of this register: what is out of
         cover, what is old enough to plan a replacement for, and what is sitting
         in a cupboard. The prototype's `#dSeg`. */
      if (lens === 'warranty' && daysUntil(d.warranty) > 90) return false;
      if (lens === 'old' && yearsOld(d.bought) < 4) return false;
      if (lens === 'unassigned' && d.user) return false;
      if (lens === 'left' && !hasLeftIntune(d) && !hasLeftNinja(d)) return false;
      if (!q) return true;
      return (
        d.tag.toLowerCase().includes(q) ||
        (d.hostName ?? '').toLowerCase().includes(q) ||
        d.serial.toLowerCase().includes(q) ||
        d.model.toLowerCase().includes(q) ||
        d.make.toLowerCase().includes(q) ||
        personName(d.user).toLowerCase().includes(q)
      );
    });
  }, [devices.data, scope, type, lens, query, personName]);

  const closeDrawer = () => {
    setSelected(null);
    if (tag) navigate('/devices', { replace: true });
  };

  const closeEditor = () => {
    setEditing(null);
    setAdding(false);
  };

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.devices });
    void qc.invalidateQueries({ queryKey: qk.audit });
    closeEditor();
    setSelected(null);
  };

  const save = useMutation({

    meta: { ok: 'Device saved' },
    mutationFn: (values: Record<string, unknown>) => {
      const patch = values as unknown as Omit<IDevice, 'id'>;
      return editing ? svc.devices.update(editing.id, patch) : svc.devices.create(patch);
    },
    onSuccess: refresh,
  });

  /* Three columns worth setting forty at a time, and all three are Ledger's
     own — nothing in Intune or NinjaOne has an opinion about where a machine
     lives, what it is for, or who paid for it, so these changes stand rather
     than being recomputed overnight. The spec sheet, compliance and last-seen
     are sync-owned and deliberately absent. */
  const depots = useDepots();
  const bulk = useBulk({
    rows: devices.data ?? [],
    idOf: (d) => d.id,
    nameOf: (d) => d.tag,
    fields: {
      loc: {
        label: 'Depot',
        options: depots.map((x) => ({ value: x.id, label: x.name })),
        note: 'Depot is yours — no sync has an opinion about where a machine lives.',
      },
      status: {
        label: 'Status',
        options: DEVICE_STATUS.map((x) => ({ value: x, label: x })),
        note: 'Status is yours and stands. Compliance and last seen come from Intune and are not offered here.',
      },
      ownership: {
        label: 'Owned or hired',
        options: [
          { value: 'Purchased', label: 'Purchased' },
          { value: 'Hired', label: 'Hired' },
        ],
        note: 'Yours. Changing it to Hired swaps the purchase and warranty dates for the hire contract on each record.',
      },
    },
    update: (id, patch) => svc.devices.update(id, patch as Partial<IDevice>),
    remove: (id) => svc.devices.remove(id),
    invalidate: [qk.devices, qk.audit],
  });

  const removeDevice = useMutation({

    meta: { ok: 'Device deleted' },
    mutationFn: (id: string) => svc.devices.remove(id),
    onSuccess: refresh,
  });

  useOpenRecord(
    devices.data ?? [],
    (device, value) => device.id === value || device.tag.toLowerCase() === value.toLowerCase(),
    (device) => {
      setSelected(device);
    },
  );

  const columns: Column<IDevice>[] = [
    {
      key: 'tag',
      header: 'Asset',
      sortBy: (d) => renamedTo(d) ?? d.tag,
      render: (d) => (
        <div className={s.person}>
          <span className={s.chip}>
            <i className={`ti ${DEVICE_ICON[d.type] ?? 'ti-device-desktop'}`} aria-hidden />
          </span>
          <div>
            <div className={`${s.cellMain} ${s.mono}`}>{renamedTo(d) ?? d.tag}</div>
            {/* Led by the name it answers to now, with the asset tag beneath when they differ.
                HUL-6591-LT sat on the register as HIRD-990 and could not be found by the name
                it was called. */}
            <div className={`${s.cellSub} ${s.mono}`}>
              {renamedTo(d) ? `Asset ${d.tag} · ` : ''}{d.ip || d.serial}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'model',
      priority: 2,
      header: 'Make & model',
      sortBy: (d) => `${d.make} ${d.model}`,
      render: (d) => (
        <>
          <div>
            {d.make} {d.model}
          </div>
          <div className={s.cellSub}>
            {d.type}
            {d.os && d.os !== '—' ? ` · ${d.os}` : ''}
          </div>
        </>
      ),
    },
    {
      key: 'age',
      priority: 3,
      header: 'Age',
      sortBy: (d) => d.bought,
      render: (d) => (
        <>
          <div>
            {d.bought && d.bought !== '—' ? `${(daysSince(d.bought) / 365).toFixed(1)} yrs` : '—'}
          </div>
          <div className={s.cellSub}>{fmtDate(d.bought)}</div>
        </>
      ),
    },
    {
      key: 'user',
      header: 'Assigned to',
      sortBy: (d) => personName(d.user),
      render: (d) => (d.user ? personName(d.user) : <Badge tone="muted">Infrastructure</Badge>),
    },
    { key: 'loc', header: 'Depot', sortBy: (d) => d.loc, render: (d) => svc.depots.name(d.loc) },
    {
      key: 'warranty',
      header: 'Warranty',
      sortBy: (d) => d.warranty,
      /* The date, not a countdown — you order a replacement against a date, and
         only the last two months want counting. */
      render: (d) => {
        const left = daysUntil(d.warranty);
        return (
          <Badge tone={expiryTone(d.warranty)}>
            {left < 0
              ? `Expired ${fmtDate(d.warranty)}`
              : left < 60
                ? `${left} days left`
                : fmtDate(d.warranty)}
          </Badge>
        );
      },
    },
    {
      key: 'compliance',
      header: 'State',
      sortBy: (d) => d.compliance,
      render: (d) => {
        const stale = daysSince(d.seen) > STALE_DAYS;
        return (
          <div className={s.tags}>
            <Badge tone={complianceTone(d.compliance)}>{d.compliance}</Badge>
            {stale && <Badge tone="warn" icon="ti-clock-exclamation">{`${daysSince(d.seen)}d`}</Badge>}
            {/* Shown right here rather than anywhere else on the row, because
                this is the cell it makes untrue: compliance and encryption come
                from Intune alone, so once a machine has left, both are frozen
                at whatever they last said and keep reporting it. */}
            {hasLeftIntune(d) && (
              <Badge tone="bad" title={`Intune stopped listing this on ${fmtDate(d.leftIntune!)} — this state is no longer being checked`}>
                Left Intune
              </Badge>
            )}
            {hasLeftNinja(d) && (
              <Badge tone="bad" title={`NinjaOne stopped reporting this on ${fmtDate(d.leftNinja!)}. If it was replaced or renamed, find the new row and retire this one.`}>
                Left NinjaOne
              </Badge>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Devices</h1>
          <p>
            Everything with a serial number — endpoints, servers, network kit, printers.
            Purchase and warranty dates are entered here; compliance and last seen come from
            Intune.
          </p>
        </div>
        <Button primary icon="ti-plus" onClick={() => { setEditing(null); setAdding(true); }}>
          New device
        </Button>
      </div>

      <Controls
        count={`${rows.length} of ${devices.data?.length ?? 0}${scope !== 'all' ? ` at ${svc.depots.name(scope)}` : ''}`}
      >
        <SearchBox value={query} onChange={setQuery} placeholder="Asset tag, serial, model, holder, IP…" />
        <Select
          label="Type"
          value={type}
          onChange={setType}
          options={[{ value: 'all', label: 'All types' }, ...types.map((t) => ({ value: t, label: t }))]}
        />
        <Segmented
          label="Lens"
          value={lens}
          onChange={setLens}
          options={[
            { value: 'all', label: 'All' },
            { value: 'warranty', label: 'Warranty due' },
            { value: 'old', label: 'Over 4 yrs' },
            { value: 'unassigned', label: 'Unassigned' },
            /* Enrolled once and no longer listed. Compliance and encryption
               are Intune's alone, so on these rows they are frozen at
               whatever they last said and stop being true from this date. */
            { value: 'left', label: 'Left Intune / Ninja' },
          ]}
        />
      </Controls>

      <BulkBar
        bulk={bulk}
        onDelete={() => {
          const n = bulk.ticked.size;
          /* Says what it does not do. A device deleted here is still enrolled in
             Intune and still on NinjaOne, and the next sync will rebuild the row
             from them — blank, without the purchase date, warranty or notes that
             only ever lived in Ledger. */
          return window.confirm(
            `Remove ${n} ${n === 1 ? 'device' : 'devices'} from ${PRODUCT.name}?` +
              '\n\nThis unenrols nothing and deletes nothing in Intune or NinjaOne — it only removes the record here.' +
              '\n\nAny machine still managed will be recreated at the next sync, without the purchase date, warranty or notes.',
          );
        }}
      />

      <Panel flush>
        {devices.isLoading ? (
          <TableSkeleton />
        ) : devices.isError ? (
          <LoadError error={devices.error} what="devices" />
        ) : (
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(d) => d.id}
            initialSort={{ key: 'tag', dir: 'asc' }}
            checked={bulk.ticked}
            onCheckedChange={bulk.setTicked}
            onRowClick={(d) => {
              setSelected(d);
            }}
            selectedKey={selected?.id}
            empty={
              <EmptyState icon="ti-device-laptop-off" title="No devices match">
                Try clearing the filters, or widen the depot scope in the header.
              </EmptyState>
            }
          />
        )}
      </Panel>

      <DeviceDetail device={selected} onClose={closeDrawer} onEdit={(d) => setEditing(d)} />
      <EditDrawer
        open={editing !== null || adding}
        record={editing}
        fields={deviceFields(useDepots(), ENDPOINT_TYPES, valuesSeen(devices.data, DEVICE_SEEN))}
        lookups={{ people: people.data ?? [], suppliers: [] }}
        noun="device"
        icon="ti-device-laptop"
        title={editing ? `Edit ${editing.tag}` : 'New device'}
        onClose={closeEditor}
        onSave={(values) => save.mutate(values)}
        onDelete={editing ? () => removeDevice.mutate(editing.id) : undefined}
        saving={save.isPending}
        deleting={removeDevice.isPending}
        error={save.error}
      >
        <Explain title="Why OS, compliance and last-seen are not on this form">
          The nightly Intune sync owns them, so anything typed in would be gone by morning.
          Purchase date and warranty are yours and nothing else maintains them — the warranty date
          in particular is what the whole expiry list runs on.
        </Explain>
      </EditDrawer>
    </>
  );
}
